/**
 * Collection banner parallax (artist + standard collection banners) and artist hero sync.
 */
(function () {
  const BANNER_SELECTOR = '.js-artist-banner-parallax, .js-collection-banner-parallax';
  const MEDIA_SELECTOR = '.artist-collection-banner__banner-media, .collection-banner-bg-img--parallax, .collection-banner-bg-img';
  const SYNC_SELECTOR = '.artist-collection-banner--sync-reveal';
  const HERO_IMG_SELECTOR =
    '.artist-collection-banner__banner-media img, .artist-collection-banner__portrait img';
  const SYNC_TIMEOUT_MS = 5000;
  const instances = new WeakMap();
  const syncPending = new WeakSet();

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animationsDisabled() {
    if (prefersReducedMotion()) return true;
    if (document.body.classList.contains('theme-animations-off')) return true;
    if (window.theme && window.theme.animationsEnable === false) return true;
    return false;
  }

  function parallaxAllowed() {
    if (animationsDisabled()) return false;
    return true;
  }

  function getTranslateY(banner, speed) {
    const rect = banner.getBoundingClientRect();
    const maxShift = Math.max(banner.offsetHeight * 0.22, 48);
    const y = Math.round((rect.top * speed) / 10);
    return Math.max(-maxShift, Math.min(maxShift, y));
  }

  function ArtistBannerParallax(banner) {
    this.banner = banner;
    this.media = banner.querySelector(MEDIA_SELECTOR);
    this.speed = parseFloat(banner.dataset.parallaxSpeed);
    if (Number.isNaN(this.speed)) this.speed = -7;
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
  }

  ArtistBannerParallax.prototype.update = function () {
    this.ticking = false;
    if (!this.media || this.speed === 0) return;

    const rect = this.banner.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;

    const y = getTranslateY(this.banner, this.speed);
    this.media.style.transform = 'translate3d(0,' + y + 'px,0)';
  };

  ArtistBannerParallax.prototype.onScroll = function () {
    if (!this.ticking) {
      this.ticking = true;
      window.requestAnimationFrame(this.update);
    }
  };

  ArtistBannerParallax.prototype.bind = function () {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    window.addEventListener('load', this.onScroll, { passive: true });
    this.update();
  };

  ArtistBannerParallax.prototype.destroy = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('load', this.onScroll);
    if (this.media) this.media.style.transform = '';
  };

  function initBanner(banner) {
    if (!banner || instances.has(banner)) return;

    const media = banner.querySelector(MEDIA_SELECTOR);
    if (!media) return;

    const instance = new ArtistBannerParallax(banner);
    instances.set(banner, instance);
    instance.bind();
  }

  function destroyIn(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll(BANNER_SELECTOR).forEach((banner) => {
      const instance = instances.get(banner);
      if (instance) {
        instance.destroy();
        instances.delete(banner);
      }
    });
  }

  function themeArtistBannerParallaxInit(scope) {
    destroyIn(scope);

    if (!parallaxAllowed()) return;

    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll(BANNER_SELECTOR).forEach(initBanner);
  }

  window.themeArtistBannerParallaxInit = themeArtistBannerParallaxInit;

  function markBlurLoaded(img) {
    img.classList.add('is-loaded');
    const wrap = img.closest('.theme-blur-up');
    if (wrap) wrap.classList.add('is-loaded');
  }

  function revealHeroSection(section) {
    section.classList.add('is-hero-ready');
    section.querySelectorAll(HERO_IMG_SELECTOR).forEach(markBlurLoaded);
    const card = section.querySelector('.artist-collection-banner__card.js-scroll-reveal');
    if (card) {
      card.classList.add('js-scroll-reveal--visible');
      card.dataset.srObserved = 'true';
    }
  }

  function syncHeroReveal(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll(`${SYNC_SELECTOR}:not(.is-hero-ready)`).forEach((section) => {
      if (syncPending.has(section)) return;
      syncPending.add(section);

      if (animationsDisabled()) {
        revealHeroSection(section);
        return;
      }

      const imgs = section.querySelectorAll(HERO_IMG_SELECTOR);
      if (!imgs.length) {
        revealHeroSection(section);
        return;
      }

      let loaded = 0;
      let revealed = false;
      const total = imgs.length;

      function finish() {
        if (revealed) return;
        revealed = true;
        revealHeroSection(section);
      }

      const timeout = window.setTimeout(finish, SYNC_TIMEOUT_MS);

      function onImgDone() {
        loaded += 1;
        if (loaded >= total) {
          window.clearTimeout(timeout);
          finish();
        }
      }

      imgs.forEach((img) => {
        if (img.complete && img.naturalWidth > 0) onImgDone();
        else {
          img.addEventListener('load', onImgDone, { once: true });
          img.addEventListener('error', onImgDone, { once: true });
        }
      });
    });
  }

  window.themeArtistBannerSyncReveal = syncHeroReveal;

  function loadArtistVideo(modal) {
    const container = modal.querySelector('[data-artist-video-container]');
    const template = modal.querySelector('.artist-video-modal__template');
    if (!container) return null;

    if (container.dataset.loaded !== 'true') {
      if (template && template.content.childElementCount > 0) {
        const clone = template.content.cloneNode(true);
        const videoEl = clone.querySelector('video');
        if (videoEl) {
          videoEl.classList.add('artist-video-modal__video');
          videoEl.muted = false;
          videoEl.defaultMuted = false;
          videoEl.removeAttribute('muted');
        }
        container.appendChild(clone);
        template.remove();
        container.dataset.loaded = 'true';
      } else {
        return null;
      }
    }

    const video = container.querySelector('video');
    if (video) {
      video.classList.add('artist-video-modal__video');
      video.muted = false;
      video.defaultMuted = false;
      video.volume = 1;
      video.play().catch(function () {});
    }
    return video;
  }

  function initArtistVideo(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll('.artist-collection-banner__portrait-opener').forEach((opener) => {
      if (opener.dataset.videoInit === 'true') return;

      const modalSelector = opener.getAttribute('data-modal');
      const modal = modalSelector ? document.querySelector(modalSelector) : null;
      if (!modal) return;

      opener.dataset.videoInit = 'true';

      const observer = new MutationObserver(function () {
        if (modal.hasAttribute('open')) {
          document.body.classList.add('overflow-hidden');
          loadArtistVideo(modal);
        } else {
          document.body.classList.remove('overflow-hidden');
        }
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  }

  window.themeArtistBannerVideoInit = initArtistVideo;

  function boot() {
    syncHeroReveal(document);
    themeArtistBannerParallaxInit(document);
    initArtistVideo(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', boot);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.nodeType === 1) {
      syncHeroReveal(event.target);
      themeArtistBannerParallaxInit(event.target);
      initArtistVideo(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target && event.target.nodeType === 1) {
      destroyIn(event.target);
    }
  });
})();
